import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import TableSortUp from '/assets/icon/global/table-sort-up.svg';
import TableSortDown from '/assets/icon/global/table-sort-down.svg';
import TableSortNo from '/assets/icon/global/table-sort-no.svg';

import ReactDOM from 'react-dom';
import CustomSelectSingle from './CustomSelectSingle.tsx';
import { TextButton } from 'components/index.js';

const EWSTooltip: React.FC<{ text: string }> = ({ text }) => {
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    const el = triggerRef.current;
    if (!el) return;
    setIsOverflow(el.scrollWidth > el.clientWidth);
  }, [text]);

  const handleMouseEnter = () => {
    if (!isOverflow) return;
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2
    });
    setVisible(true);
  };

  const handleMouseLeave = () => {
    setVisible(false);
  };

  return (
      <>
      <span
          ref={triggerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            display: 'inline-block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle'
          }}
          title={isOverflow ? text : undefined}
      >
        {text}
      </span>
        {visible &&
            isOverflow &&
            text &&
            ReactDOM.createPortal(
                <div
                    style={{
                      position: 'fixed',
                      top: position.top,
                      left: position.left,
                      transform: 'translateX(-50%)',
                      backgroundColor: '#FFF',
                      color: '#000',
                      fontSize: '15px',
                      padding: '10px 10px',
                      borderRadius: '4px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      pointerEvents: 'none',
                      zIndex: 9999,
                      boxShadow: '0 4px 8px rgba(0,0,0,0.25)'
                    }}
                >
                  {text}
                  <div
                      style={{
                        position: 'absolute',
                        top: -6,
                        left: '50%',
                        transform: 'translateX(-50%) rotate(180deg)',
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderTop: '6px solid #FFF'
                      }}
                  />
                </div>,
                document.body
            )}
      </>
  );
};

type LeafColumn = {
  column: string;
  label: string | React.ReactNode;
  title?: string;
  align?: string;
  size?: number | string;
  type?: string;
  sort?: boolean;
  treeColumn?: any;
  custom?: any;
  buttonText?: string;
  buttonAction?: any;
  isShowHeaderButton?: boolean;
  onHeaderButtonClick?: any;
  decimals?: number;
};

type GroupColumn = {
  label: string | React.ReactNode;
  align?: string;
  children: LeafColumn[];
};

type ColumnDef = LeafColumn | GroupColumn;

const isGroupColumn = (c: ColumnDef): c is GroupColumn => {
  return (c as GroupColumn).children != null;
};

interface IProps {
  className?: string;
  style?: React.CSSProperties;

  //table 관련
  columns: ColumnDef[];
  data: Record<string, any>[];
  setData?: any;
  actions?: any;
  click?: (
      row: Record<string, any>,
      rowIndex: number,
      e: React.MouseEvent<HTMLTableDataCellElement>
  ) => void;
  dbClick?: (
      row: Record<string, any>,
      rowIndex: number,
      e: React.MouseEvent<HTMLTableDataCellElement>
  ) => void;
  isCheck?: boolean;
  checkColumn?: string;
  checkedList?: any[];
  setCheckedList?: (list: any[]) => void;
  setSelectedRow?: (item: any) => void;
  setTableSort?: (list: any[]) => void;
  isDisabledCheckRow?: (row: Record<string, any>) => boolean;
  bodyHeight?: number;

  //paging관련
  isPaging: boolean;
  totalCount?: number;
  pageSize?: number;
  setPageSize?: any;
  currentPage?: number;
  setCurrentPage?: Dispatch<SetStateAction<number>>;
  infoText?: string;
}

const defaultNumBtnCnt = 5;

const CustomTable = (props: IProps) => {
  const [totalPages, setTotalPages] = useState<number>(0);
  const [numBtnCnt, setNumBtnCnt] = useState<number>(5);
  const [minBtnNum, setMinBtnNum] = useState<number>(1);

  const masterCheckRef = useRef<HTMLInputElement | null>(null);
  const rowChecksRef = useRef<Array<HTMLInputElement | null>>([]);

  const [selectedRowIdx, setSelectedRowIdx] = useState<number | null>(null);

  const [sortData, setSortData] = useState<any>({});

  useEffect(() => {
    setSelectedRowIdx(null);
    emitCheckedRows();
  }, [props?.data]);

  // checkedList가 있는경우 동기화 하기위해 사용. checkedList가 필요없으면 사용안됨
  useEffect(() => {
    if (!props.checkedList) return;

    props.data.forEach((row, idx) => {
      const checkbox = rowChecksRef.current[idx];

      if (checkbox) {
        let isChecked: boolean;
        isChecked = props.checkedList ? props.checkedList?.includes(row) : false;
        checkbox.checked = isChecked;
      }
    });

    if (masterCheckRef.current) {
      masterCheckRef.current.checked =
          props.data.length > 0 && props.data.every((row) => props.checkedList?.includes(row));
    }
  }, [props.checkedList, props.data]);

  useEffect(() => {
    if (!props.isPaging || !props.totalCount || !props.pageSize) return;

    // 1) totalPages 계산
    const total = Math.max(1, Math.ceil(props.totalCount / props.pageSize));
    setTotalPages(total);

    // 2) currentPage 보정
    const cur = Math.min(Math.max(1, props.currentPage ?? 1), total);

    // 3) 블록 시작/끝 → 버튼 개수
    const per = defaultNumBtnCnt;
    const start = Math.floor((cur - 1) / per) * per + 1;
    const end = Math.min(total, start + per - 1);

    setMinBtnNum(start);
    setNumBtnCnt(end - start + 1);
  }, [props.isPaging, props.totalCount, props.pageSize, props.currentPage]);

  useEffect(() => {
    props.setTableSort?.(sortData);
  }, [sortData]);

  // 그룹/단일 컬럼을 모두 "최종(leaf) 컬럼" 배열로 펼침
  const leafColumns: LeafColumn[] = React.useMemo(() => {
    const out: LeafColumn[] = [];
    props.columns.forEach((c) => {
      if (isGroupColumn(c)) out.push(...c.children);
      else out.push(c);
    });
    return out;
  }, [props.columns]);

  // 2뎁스 헤더 여부(그룹 컬럼이 하나라도 있으면 2줄 헤더 렌더)
  const hasGroupedHeader = React.useMemo(() => props.columns.some(isGroupColumn), [props.columns]);

  const movePage = (pageNum: number) => {
    if (props.setCurrentPage) {
      props.setCurrentPage(pageNum);
    }
  };

  const moveStart = () => {
    if (props.setCurrentPage) {
      props.setCurrentPage(1);
    }
  };

  const moveLeft = () => {
    if (!props.currentPage || !props.setCurrentPage) return;
    const tempCurrentPage = minBtnNum - 1;

    if (tempCurrentPage < 1) return;
    props.setCurrentPage(tempCurrentPage);
  };

  const moveRight = () => {
    if (!props.setCurrentPage || !props.currentPage) return;
    const tempCurrentPage = minBtnNum + defaultNumBtnCnt;

    if (props.totalCount == 0) return;
    if (props.totalCount && totalPages < tempCurrentPage) return;
    props.setCurrentPage(tempCurrentPage);
  };

  const moveEnd = () => {
    if (props.setCurrentPage && totalPages) {
      props.setCurrentPage(totalPages);
    }
  };

  const onToggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    props.setData?.((prev: any[]) =>
        prev.map((row) =>
            props.isDisabledCheckRow?.(row)
                ? row // 비활성화된 행은 변경하지 않음
                : { ...row, [props.checkColumn || 'isCheck']: checked }
        )
    );
    if (masterCheckRef?.current && 'checked' in masterCheckRef.current) {
      masterCheckRef.current.checked = e.target.checked;
    }
  };

  const syncMasterFromRows = (rowIndex: number, e: any) => {
    if (e.target.type == 'checkbox') {
      props.setData?.((prev: any[]) =>
          prev.map((r, i) =>
              i === rowIndex ? { ...r, [props.checkColumn || 'isCheck']: e.target.checked } : r
          )
      );
    }
  };

  // 현재 체크된 행들을 수집해서 부모에 전달
  const emitCheckedRows = () => {
    const list = props.data.filter((_, i) => {
      const el = rowChecksRef.current[i];
      return el && !el.disabled && el.checked;
    });
    props.setCheckedList?.(list);
  };

  const toCssSize = (s?: number | string) => (typeof s === 'number' ? `${s}px` : s);

  const selectedRow = (row: any, idx: number, e: React.MouseEvent<HTMLTableDataCellElement>) => {
    setSelectedRowIdx(idx);

    props.setSelectedRow?.(row);
    props.click?.(row, idx, e);
  };

  const clickSort = (column: string) => {
    if (sortData?.sortColumn == column) {
      if (sortData?.sortValue == 'ASC') {
        setSortData({ sortColumn: column, sortValue: 'DESC' });
      } else if (sortData?.sortValue == 'DESC') {
        setSortData({});
      }
    } else {
      setSortData({ sortColumn: column, sortValue: 'ASC' });
    }
  };

  //트리 생성 기능
  const getLevel = (row: any, levelKey: string) => Math.max(1, Number(row[levelKey] ?? 1));

  const hasDirectChild = (rows: any[], idx: number, levelKey: string) => {
    const curLv = getLevel(rows[idx], levelKey);
    for (let i = idx + 1; i < rows.length; i++) {
      const lv = getLevel(rows[i], levelKey);
      if (lv <= curLv) return false; // 같은 레벨 or 상위로 돌아오면 종료 (자식 없음)
      if (lv === curLv + 1) return true; // 바로 다음으로 깊어진 첫 레벨이 직계 자식
      // lv > curLv + 1 인 경우는 스킵(잘못 정렬된 데이터가 아니면 거의 없음)
    }
    return false;
  };

  return (
      <>
        <div className={`custom-table ${props.className || ''}`} style={props.style}>
          {/*<div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div className={'paging-result-info gap-2'}>
            {props.setPageSize && <div></div>}

            {props.totalCount ? (
              <div>
                {props?.data?.length}건 / 총 {props?.totalCount}건
              </div>
            ) : (
              <div>총 {props?.data?.length}건</div>
            )}
          </div>
          <div style={{ display: 'flex' }}>{props.actions}</div>

          {props.infoText && (
            <div
              style={{
                fontSize: '15px',
                fontWeight: 400,
                color: '#FFFFFF',
                opacity: 0.6
              }}
            >
              {props.infoText}
            </div>
          )}
        </div>*/}
          <div className="table-scroll">
            <table className={'custom-table-inner'}>
              <colgroup>
                {props.isCheck && <col style={{ width: '50px' }} />}
                {leafColumns.map((col, idx) => (
                    <col key={idx} style={{ width: toCssSize(col.size) }} />
                ))}
              </colgroup>
              <thead>
              {/* 1번째 줄: 그룹 헤더(또는 단일 컬럼은 rowSpan 처리) */}
              <tr>
                {props.isCheck && (
                    <th
                        rowSpan={hasGroupedHeader ? 2 : 1}
                        className={'c-pointer'}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).tagName === 'INPUT') return;
                          masterCheckRef.current?.click();
                        }}
                    >
                      <input ref={masterCheckRef} type={'checkbox'} onChange={onToggleAll} />
                    </th>
                )}

                {props.columns.map((col, idx) => {
                  // 그룹 컬럼
                  if (isGroupColumn(col)) {
                    return (
                        <th
                            key={`g-${idx}`}
                            style={{
                              textAlign: 'center' as React.CSSProperties['textAlign']
                            }}
                            colSpan={col.children.length}
                        >
                          <div>{col.label}</div>
                        </th>
                    );
                  }

                  // 단일(leaf) 컬럼
                  return (
                      <th
                          key={`l-${idx}`}
                          rowSpan={hasGroupedHeader ? 2 : 1}
                          style={{
                            textAlign: 'center' as React.CSSProperties['textAlign'],
                            cursor: col.sort ? 'pointer' : ''
                          }}
                          title={col.title}
                          onClick={() => {
                            if (col.sort) clickSort(col.column);
                          }}
                      >
                        {col.isShowHeaderButton ? (
                            <TextButton
                                variant="contained"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  col.onHeaderButtonClick?.();
                                }}
                            >
                              {col.label}
                            </TextButton>
                        ) : (
                            <div>{col.label}</div>
                        )}
                        {col.sort && (
                            <div className={'table-sort'}>
                              {(sortData?.sortColumn == null || sortData?.sortColumn != col.column) && (
                                  <div>
                                    <img src={TableSortNo} alt={'TableSortNo'} />
                                  </div>
                              )}

                              {sortData?.sortColumn == col.column && sortData?.sortValue == 'ASC' && (
                                  <div>
                                    <img src={TableSortUp} alt={'TableSortUp'} />
                                  </div>
                              )}

                              {sortData?.sortColumn == col.column && sortData?.sortValue == 'DESC' && (
                                  <div>
                                    <img src={TableSortDown} alt={'TableSortDown'} />
                                  </div>
                              )}
                            </div>
                        )}
                      </th>
                  );
                })}
              </tr>

              {/* 2번째 줄: 그룹의 children 헤더들 */}
              {hasGroupedHeader && (
                  <tr>
                    {props.columns.flatMap((col, idx) => {
                      if (!isGroupColumn(col)) return [];

                      return col.children.map((child, cIdx) => (
                          <th
                              key={`c-${idx}-${cIdx}`}
                              style={{
                                textAlign: (col.align ?? 'center') as React.CSSProperties['textAlign'],
                                cursor: child.sort ? 'pointer' : ''
                              }}
                              onClick={() => {
                                if (child.sort) clickSort(child.column);
                              }}
                          >
                            <div>{child.label}</div>

                            {/*{child.sort && (
                          <div className={'table-sort'}>
                            {(sortData?.sortColumn == null ||
                              sortData?.sortColumn != child.column) && (
                              <div>
                                <TableSortNo />
                              </div>
                            )}

                            {sortData?.sortColumn == child.column &&
                              sortData?.sortValue == 'ASC' && (
                                <div>
                                  <TableSortUp />
                                </div>
                              )}

                            {sortData?.sortColumn == child.column &&
                              sortData?.sortValue == 'DESC' && (
                                <div>
                                  <TableSortDown />
                                </div>
                              )}
                          </div>
                        )}*/}
                          </th>
                      ));
                    })}
                  </tr>
              )}
              </thead>
              <tbody
                  style={{
                    maxHeight:
                        props.bodyHeight != null ? props.bodyHeight + 'px' : 'calc(100vh - 400px)'
                  }}
              >
              {props?.data?.map((row, idx) => (
                  <tr
                      key={idx}
                      className={selectedRowIdx === idx ? 'active' : ''}
                      //style={{ cursor: props.dbClick || props.setSelectedRow ? "pointer" : undefined }}
                      style={{ cursor: 'pointer' }}
                  >
                    {props.isCheck && (
                        <td
                            onClick={(e) => {
                              if ((e.target as HTMLElement).tagName === 'INPUT') return;
                              rowChecksRef.current[idx]?.click();
                            }}
                            style={{ width: '50px' }}
                        >
                          <input
                              type="checkbox"
                              ref={(el: HTMLInputElement | null) => {
                                rowChecksRef.current[idx] = el;
                              }}
                              disabled={props.isDisabledCheckRow?.(row) ?? false}
                              style={{ visibility: (props.isDisabledCheckRow?.(row) ?? false) ? 'hidden' : 'visible' }}
                              onChange={(e: any) => {
                                syncMasterFromRows(idx, e);
                              }}
                              checked={props?.data[idx][props.checkColumn || 'isCheck'] || false}
                          />
                        </td>
                    )}
                    {leafColumns.map((item, colIdx) => (
                        <td
                            key={colIdx}
                            style={{
                              textAlign: (item.align ?? 'center') as React.CSSProperties['textAlign'],
                              width: toCssSize(item.size)
                            }}
                            onDoubleClick={(e) => {
                              if (item?.type != 'button') {
                                props.dbClick?.(row, idx, e);
                              }
                            }}
                            onClick={(e) => {
                              if (item?.type != 'button') {
                                selectedRow(row, idx, e);
                              }
                            }}
                        >
                          {item?.treeColumn ? (
                              (() => {
                                const levelKey = item.treeColumn as string;
                                const level = getLevel(row, levelKey);
                                const hasChild = hasDirectChild(props.data, idx, levelKey);

                                return (
                                    <span
                                        style={{
                                          paddingLeft: `${(level - 1) * 32}px`,
                                          whiteSpace: 'nowrap'
                                        }}
                                    >
                              {/*{hasChild ? (
                                <GridTreeIcon
                                  width={12}
                                  height={12}
                                  style={{ marginRight: '10px' }}
                                />
                              ) : (
                                ''
                              )}*/}
                                      {row[item.column]}
                            </span>
                                );
                              })()
                          ) : item.type == 'button' ? (
                              <button
                                  className={'inner-btn c-pointer'}
                                  onClick={() => {
                                    item?.buttonAction(row);
                                  }}
                              >
                                {item?.buttonText}
                              </button>
                          ) : item.type == 'custom' ? (
                              item?.custom(row, idx)
                          ) : (
                              item.decimals ?
                                  <EWSTooltip text={(+row[item.column]).toFixed(item.decimals)} />:
                                  <EWSTooltip text={row[item.column]} />
                          )}
                        </td>
                    ))}
                  </tr>
              ))}

              {props.data?.length == 0 && (
                  <tr>
                    <td
                        colSpan={leafColumns.length + (props.isCheck ? 1 : 0)}
                        style={{ textAlign: 'center' }}
                    >
                      No results found.
                    </td>
                  </tr>
              )}
              </tbody>
            </table>
          </div>

          {props.isPaging && props?.totalCount && props?.totalCount > 0 ? (
              <div className="paging">
                <div className={'paging-per-page'}>
                  <span className={'item-per-page-text'}>Items per page</span>
                  <CustomSelectSingle
                      value={props?.pageSize || 10}
                      onChange={(e: any) => {
                        const size = Number(e.target.value);
                        props.setPageSize(size);
                        props.setCurrentPage?.(1);
                      }}
                      options={[
                        { label: '5', value: 5 },
                        { label: '10', value: 10 },
                        { label: '20', value: 20 },
                        { label: '30', value: 30 },
                        { label: '50', value: 50 },
                        { label: '100', value: 100 }
                      ]}
                      isClearable={false}
                      style={{ width: '80px' }}
                  ></CustomSelectSingle>
                </div>

                {/*<button className={'paging-btn-start'} onClick={moveStart}></button>*/}
                <button className={'paging-btn-left'} onClick={moveLeft}></button>
                <div className="paging-btn-center">
                  {[...Array(numBtnCnt)]?.map((value, index) => {
                    return (
                        <button
                            key={index}
                            className={`page-number ${props.currentPage == minBtnNum + index ? 'active' : ''}`}
                            onClick={() => {
                              movePage(minBtnNum + index);
                            }}
                        >
                          {minBtnNum + index}
                        </button>
                    );
                  })}
                </div>
                <button className={'paging-btn-right'} onClick={moveRight}></button>
                {/*<button className={'paging-btn-end'} onClick={moveEnd}></button>*/}
              </div>
          ) : null}
        </div>
      </>
  );
};

export default CustomTable;
